import { apiFetch } from './api';
import type { Hotel } from '@/types/travel';

export interface HotelSearchParams {
  destination: string;
  checkin: string;
  checkout: string;
  guests: Array<{ adults: number; children: number[] }>;
}

/** VOY-299: Taxes paid on arrival (included_by_supplier: false), excluded from total */
export interface TaxesOnArrival {
  amount: number;
  currency: string;
  items?: Array<{ amount: number; currency: string; name?: string }>;
}

/** Raw tax item from RateHawk API (fallback parsing) */
interface RatehawkTaxItem {
  amount?: number | string;
  currency_code?: string;
  currency?: string;
  name?: string;
  included_by_supplier?: boolean;
}

/** Raw payment type from RateHawk (fallback parsing) */
interface RatehawkPaymentType {
  tax_data?: { taxes?: RatehawkTaxItem[] };
  show_amount?: number | string;
  amount?: number | string;
  show_currency_code?: string;
  currency_code?: string;
  cancellation_penalties?: {
    policies?: Array<{ deadline?: string; date?: string; timezone?: string; time_zone?: string; description?: string; amount?: unknown }>;
    free_cancellation_before?: string;
  };
}

/** Raw rate from RateHawk API (fallback parsing) */
interface RatehawkRawRate {
  payment_options?: { payment_types?: RatehawkPaymentType[] };
  daily_prices?: unknown[];
  book_hash?: string;
  match_hash?: string;
  room_name?: string;
  room_data_trans?: { bedding_type?: string };
  rg_ext?: { capacity?: number };
  meal_type?: string;
  board?: string;
  meal?: string;
  amenities_data?: string[];
  allotment?: number;
}

/** VOY-71: if tax amount > this × median (in same currency), treat as outlier and cap to 0. No fixed cap for devalued currencies. */
const OUTLIER_MULTIPLIER = 1000;

export interface HotelRoomOption {
  id: string;
  name: string;
  description?: string;
  price: number;  // Per-night price (calculated from daily_prices or total/nights)
  totalPrice?: number;  // Total price for the stay (VOY-299: excludes non-included taxes)
  nights?: number;  // Number of nights
  originalPrice?: number;
  currency: string;
  cancellationPolicy?: string;  // VOY-301: from cancellation_penalties.policies with deadline + timezone
  taxesOnArrival?: TaxesOnArrival;  // VOY-299: taxes/fees paid at property
  mealPlan?: string;  // VOY-71: meal type as returned (e.g. breakfast, no_meal)
  amenities?: string[];
  images?: string[];
  maxOccupancy?: number;
  bedConfiguration?: string;
  size?: string;
  view?: string;
  available?: number;
  room_data_trans_id?: string;  // VOY-268: RateHawk room identifier (JSON stringified) required for /finish
}

export interface HotelDetailsResponse extends Hotel {
  rooms?: HotelRoomOption[];
  policies?: {
    checkIn?: string;
    checkOut?: string;
    cancellation?: string;
    childPolicy?: string;
    petPolicy?: string;
  };
  reviews?: {
    average: number;
    total: number;
    distribution?: Record<number, number>;
  };
}

export class HotelsAPIError extends Error {
  constructor(
    message: string,
    public statusCode?: number,
    public details?: unknown
  ) {
    super(message);
    this.name = 'HotelsAPIError';
  }
}

// Simple global concurrency limiter for hotel details requests
let activeDetailRequests = 0;
const MAX_CONCURRENT_DETAILS = 8; // Increased from 4 to 8 for better data enrichment
const detailsQueue: Array<() => void> = [];

async function runWithDetailsLimiter<T>(task: () => Promise<T>): Promise<T> {
  if (activeDetailRequests >= MAX_CONCURRENT_DETAILS) {
    await new Promise<void>((resolve) => detailsQueue.push(resolve));
  }
  activeDetailRequests++;
  try {
    const result = await task();
    return result;
  } finally {
    activeDetailRequests--;
    const next = detailsQueue.shift();
    if (next) next();
  }
}

// Unused or unreferenced check for delete
export async function searchHotelsViaBackend(params: HotelSearchParams): Promise<Hotel[]> {
  try {
    // Validate parameters
    if (!params.destination?.trim()) {
      throw new HotelsAPIError('Destination is required');
    }
    
    if (!params.checkin || !params.checkout) {
      throw new HotelsAPIError('Check-in and check-out dates are required');
    }
    
    const checkinDate = new Date(params.checkin);
    const checkoutDate = new Date(params.checkout);
    
    if (checkinDate >= checkoutDate) {
      throw new HotelsAPIError('Check-out date must be after check-in date');
    }
    
    if (checkinDate < new Date()) {
      throw new HotelsAPIError('Check-in date cannot be in the past');
    }
    
    if (!params.guests || params.guests.length === 0) {
      throw new HotelsAPIError('At least one guest is required');
    }
    
    const response = await apiFetch('/api/hotels/search', {
      method: 'POST',
      body: JSON.stringify(params),
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new HotelsAPIError(
        errorData.message || 'Failed to search hotels',
        response.status,
        errorData
      );
    }
    
    const data = await response.json();
    return data.hotels || [];
  } catch (error) {
    if (error instanceof HotelsAPIError) {
      throw error;
    }
    
    console.error('Hotel search error:', error);
    throw new HotelsAPIError(
      'An unexpected error occurred while searching hotels',
      undefined,
      error
    );
  }
}

export async function getHotelDetails(
  hotelId: string, 
  params: Omit<HotelSearchParams, 'destination'>
): Promise<HotelDetailsResponse> {
  try {
    // Validate parameters
    if (!hotelId?.trim()) {
      throw new HotelsAPIError('Hotel ID is required');
    }
    
    if (!params.checkin || !params.checkout) {
      throw new HotelsAPIError('Check-in and check-out dates are required');
    }
    
    const response = await runWithDetailsLimiter(() =>
      apiFetch('/api/hotels/details', {
        method: 'POST',
        body: JSON.stringify({ hotel_id: hotelId, ...params }),
      })
    );
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new HotelsAPIError(
        errorData.message || 'Failed to get hotel details',
        response.status,
        errorData
      );
    }
    
    const data = await response.json();
    // Backend shape: { success, data: { info, rates } }
    // rates.rooms = backend-transformed rooms (VOY-299, VOY-301); rates.data = raw RateHawk response
    const info = data?.data?.info?.data || {};
    const ratesPayload = data?.data?.rates || {};
    const backendRooms = ratesPayload.rooms;
    const rates = ratesPayload.data || {};
    // VOY-393: Review score (0-10) from backend Content API
    const backendReviewScore = data?.data?.rating;

    // Images: prefer info.images, then images_ext[].url, then []
    const rawImages: string[] =
      Array.isArray(info?.images) && info.images.length > 0
        ? info.images
        : Array.isArray(info?.images_ext)
          ? info.images_ext
              .map((img: string | { url?: string }) => (typeof img === 'string' ? img : img?.url))
              .filter(Boolean)
          : [];
    const resolvedImages = rawImages
      .map((u: string) => (typeof u === 'string' ? u.replace('{size}', '1024x768') : u))
      .filter(Boolean);

    const hotelBase: Hotel = {
      id: String(info?.hid || hotelId),
      name: info?.name || 'Hotel',
      location: info?.address || info?.region?.name || 'Unknown',
      price: { amount: 0, currency: 'USD' },
      pricePerNight: 0,
      rating: backendReviewScore || (typeof info?.star_rating === 'number' ? info.star_rating : (info?.rating || 4)),
      starRating: info?.star_rating || undefined,
      amenities: Array.isArray(info?.amenity_groups)
        ? info.amenity_groups
            .flatMap((g: { amenities?: unknown[] }) => g?.amenities || [])
            .filter(Boolean)
            .slice(0, 8)
        : [],
      images: resolvedImages,
      description: Array.isArray(info?.description_struct)
        ? info.description_struct.map((s: { paragraphs?: string[] }) => (s?.paragraphs || []).join('\n')).join('\n')
        : (typeof info?.description === 'string' && info.description.toLowerCase().includes('found via autocomplete'))
          ? undefined
          : info?.description,
    };

    // Prefer backend-transformed rooms (VOY-299, VOY-301) when available
    let rooms: HotelRoomOption[];
    if (Array.isArray(backendRooms) && backendRooms.length > 0) {
      rooms = backendRooms.map((r: Record<string, unknown>) => ({
        id: String(r.id ?? ''),
        name: String(r.name ?? 'Room'),
        description: undefined,
        price: Number(r.price ?? 0),
        totalPrice: Number(r.totalPrice ?? r.apiPrice ?? 0),
        nights: Number(r.nights ?? 1),
        originalPrice: undefined,
        currency: String(r.currency ?? 'USD'),
        cancellationPolicy: r.cancellationPolicy ? String(r.cancellationPolicy) : undefined,
        taxesOnArrival: r.taxesOnArrival as TaxesOnArrival | undefined,
        mealPlan: r.mealPlan ? String(r.mealPlan) : undefined,
        amenities: Array.isArray(r.amenities) ? r.amenities as string[] : [],
        images: [],
        maxOccupancy: typeof r.maxOccupancy === 'number' ? r.maxOccupancy : undefined,
        bedConfiguration: undefined,
        size: undefined,
        view: undefined,
        available: undefined,
        room_data_trans_id: r.room_data_trans_id ? String(r.room_data_trans_id) : String(r.id ?? ''),
      }));
    } else {
      // Fallback: transform from raw RateHawk response
      const roomRates: RatehawkRawRate[] = Array.isArray(rates?.hotels) && rates.hotels.length > 0
        ? (rates.hotels[0]?.rates || [])
        : [];

      const formatCancellationPolicy = (pt: RatehawkPaymentType | undefined) => {
        const cp = pt?.cancellation_penalties;
        if (!cp) return 'Non-refundable';
        if (Array.isArray(cp.policies) && cp.policies.length > 0) {
          return cp.policies.map((p: { deadline?: string; date?: string; timezone?: string; time_zone?: string; description?: string; amount?: unknown }) => {
            const deadline = p.deadline || p.date;
            const tz = p.timezone || p.time_zone;
            const desc = p.description || (p.amount ? `Amount: ${p.amount}` : '');
            const deadlineStr = deadline ? (tz ? `Deadline: ${deadline} (${tz})` : `Deadline: ${deadline}`) : '';
            return [deadlineStr, desc].filter(Boolean).join(' – ');
          }).join('; ') || 'Non-refundable';
        }
        if (cp.free_cancellation_before) return `Free cancellation before ${cp.free_cancellation_before}`;
        return 'Non-refundable';
      };
      // Taxes/fees paid on arrival are in local currency (e.g. HNL) even when room price is in USD.
      // Cap absurd items: (1) when tax currency matches display and amount > 2× room total; (2) outlier (amount > threshold per currency); single-item currencies use global median.
      const parseTaxesOnArrival = (pt: RatehawkPaymentType | undefined, apiPrice: number, rateDisplayCurrency: string) => {
        const taxes = pt?.tax_data?.taxes;
        if (!Array.isArray(taxes)) return null;
        const nonIncluded = taxes.filter((t: RatehawkTaxItem) => t.included_by_supplier === false);
        if (nonIncluded.length === 0) return null;
        const maxSanePerItem = apiPrice > 0 ? apiPrice * 2 : Infinity;
        const byCurrency: Record<string, number[]> = {};
        nonIncluded.forEach((t: RatehawkTaxItem) => {
          const c = t.currency_code || t.currency || 'USD';
          const amt = Number(t.amount) || 0;
          if (amt <= 0) return;
          if (!byCurrency[c]) byCurrency[c] = [];
          byCurrency[c].push(amt);
        });
        const allAmounts = Object.values(byCurrency).flat().sort((a, b) => a - b);
        const globalMedian = allAmounts.length > 0 ? allAmounts[Math.floor((allAmounts.length - 1) / 2)] : 0;
        const outlierThresholdByCurrency: Record<string, number> = {};
        Object.keys(byCurrency).forEach((c) => {
          const amounts = byCurrency[c].slice().sort((a, b) => a - b);
          if (amounts.length >= 2) {
            const median = amounts[Math.floor((amounts.length - 1) / 2)];
            const max = amounts[amounts.length - 1];
            if (median > 0 && max > OUTLIER_MULTIPLIER * median) outlierThresholdByCurrency[c] = OUTLIER_MULTIPLIER * median;
          } else if (amounts.length === 1 && globalMedian > 0 && amounts[0] > OUTLIER_MULTIPLIER * globalMedian) {
            outlierThresholdByCurrency[c] = OUTLIER_MULTIPLIER * globalMedian;
          }
        });
        const baseCurrency = nonIncluded[0]?.currency_code || nonIncluded[0]?.currency || 'USD';
        let total = 0;
        const items = nonIncluded
          .map((t: RatehawkTaxItem) => {
            const itemCurrency = t.currency_code || t.currency || 'USD';
            let amt = Number(t.amount) || 0;
            const sameCurrencyCap = itemCurrency === rateDisplayCurrency && amt > maxSanePerItem;
            const threshold = outlierThresholdByCurrency[itemCurrency];
            const outlierCap = threshold != null && amt > threshold;
            if (sameCurrencyCap || outlierCap) {
              const reasons = [sameCurrencyCap && 'same-currency cap', outlierCap && 'outlier'].filter(Boolean).join(', ');
              console.warn('[Hotels][taxes] Capping tax "%s" from %s to 0 (room total %s, reason: %s)', t.name || 'item', amt, apiPrice, reasons);
              amt = 0;
            }
            if (itemCurrency === baseCurrency) total += amt;
            return { amount: amt, currency: itemCurrency, name: t.name || '' };
          })
          .filter((it: { amount: number }) => it.amount > 0);
        if (total === 0) return null;
        return { amount: total, currency: baseCurrency, items };
      };
      rooms = roomRates.map((r: RatehawkRawRate) => {
        const pt = r?.payment_options?.payment_types?.[0];
        const apiPrice = pt?.show_amount ? Number(pt.show_amount) : (pt?.amount ? Number(pt.amount) : 0);
        const rateDisplayCurrency = pt?.show_currency_code || pt?.currency_code || 'USD';
        const taxesOnArrival = parseTaxesOnArrival(pt, apiPrice, rateDisplayCurrency);
        const totalExcludingTaxes =
          taxesOnArrival && taxesOnArrival.currency === rateDisplayCurrency
            ? Math.max(0, apiPrice - taxesOnArrival.amount)
            : apiPrice;
        const nights = Array.isArray(r?.daily_prices) ? r.daily_prices.length : 1;
        const pricePerNight = nights > 0 ? totalExcludingTaxes / nights : totalExcludingTaxes;
        const roomId = r?.book_hash || r?.match_hash || r?.room_name || Math.random().toString(36).slice(2);
        return {
          id: roomId,
          name: r?.room_name || 'Room',
          description: undefined,
          price: pricePerNight,
          totalPrice: totalExcludingTaxes,
          nights,
          originalPrice: undefined,
          currency: rateDisplayCurrency,
          cancellationPolicy: formatCancellationPolicy(pt),
          taxesOnArrival: taxesOnArrival ?? undefined,
          mealPlan: r?.meal_type ?? r?.board ?? r?.meal,
          amenities: r?.amenities_data || [],
          images: [],
          maxOccupancy: r?.rg_ext?.capacity || undefined,
          bedConfiguration: r?.room_data_trans?.bedding_type || undefined,
          size: undefined,
          view: undefined,
          available: typeof r?.allotment === 'number' ? r.allotment : undefined,
          room_data_trans_id: roomId,
        } as HotelRoomOption;
      });
    }

    // If we have rooms, set card-level price to min room price
    const priceMin = rooms.length > 0 ? Math.min(...rooms.map(r => r.price)) : 0;

    return {
      ...hotelBase,
      price: { amount: priceMin, currency: 'USD' },
      pricePerNight: priceMin,
      rooms,
      policies: {
        checkIn: info?.check_in_time,
        checkOut: info?.check_out_time,
      },
    } as HotelDetailsResponse;
  } catch (error) {
    if (error instanceof HotelsAPIError) {
      throw error;
    }
    
    // Re-throw AbortError without wrapping - component will handle it
    if (error instanceof Error && error.name === 'AbortError') {
      throw error;
    }
    
    console.error('Hotel details error:', error);
    throw new HotelsAPIError(
      'An unexpected error occurred while fetching hotel details',
      undefined,
      error
    );
  }
}

// Unused or unreferenced check for delete
export async function checkHotelAvailability(
  hotelId: string,
  roomId: string,
  params: Omit<HotelSearchParams, 'destination'>
): Promise<{ available: boolean; price?: number; message?: string }> {
  try {
    const response = await apiFetch('/api/hotels/availability', {
      method: 'POST',
      body: JSON.stringify({ hotelId, roomId, ...params }),
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new HotelsAPIError(
        errorData.message || 'Failed to check availability',
        response.status,
        errorData
      );
    }
    
    return await response.json();
  } catch (error) {
    if (error instanceof HotelsAPIError) {
      throw error;
    }
    
    console.error('Hotel availability check error:', error);
    throw new HotelsAPIError(
      'An unexpected error occurred while checking availability',
      undefined,
      error
    );
  }
}

// Unused or unreferenced check for delete
export async function createHotelBooking(
  hotelId: string,
  roomId: string,
  params: HotelSearchParams & {
    guestDetails: {
      firstName: string;
      lastName: string;
      email: string;
      phone?: string;
    };
    specialRequests?: string;
    paymentIntentId?: string;
  }
): Promise<{ bookingId: string; confirmation: string; details: unknown }> {
  try {
    const response = await apiFetch('/api/hotels/book', {
      method: 'POST',
      body: JSON.stringify({ hotelId, roomId, ...params }),
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new HotelsAPIError(
        errorData.message || 'Failed to create booking',
        response.status,
        errorData
      );
    }
    
    return await response.json();
  } catch (error) {
    if (error instanceof HotelsAPIError) {
      throw error;
    }
    
    console.error('Hotel booking error:', error);
    throw new HotelsAPIError(
      'An unexpected error occurred while creating the booking',
      undefined,
      error
    );
  }
}

// Helper function to format guest configuration for API
export function formatGuestConfiguration(
  adults: number,
  children: number[] = []
): HotelSearchParams['guests'] {
  return [{
    adults,
    children: children.map(age => Number(age))
  }];
}

// Helper function to calculate total nights
// Unused or unreferenced check for delete
export function calculateNights(checkin: string, checkout: string): number {
  const start = new Date(checkin);
  const end = new Date(checkout);
  const diffTime = Math.abs(end.getTime() - start.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
}

// Helper function to format price with currency
// Unused or unreferenced check for delete
export function formatHotelPrice(price: number, currency = 'USD', nights?: number): string {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  });
  
  if (nights && nights > 1) {
    return `${formatter.format(price)} x ${nights} nights = ${formatter.format(price * nights)}`;
  }
  
  return formatter.format(price);
}